if (!customElements.get('size-quantity-grid-submit')) {
  // Some custom-option apps (e.g. Hulk Product Options) charge a one-time
  // fee keyed off a specific line item property, applied per line item on
  // their side - repeating that property on every size added here would
  // multiply the fee. List the exact property label(s) that should only be
  // kept on the first item added; every other property still gets copied to
  // every size.
  const ONE_TIME_FEE_PROPERTIES = ['Is this a new logo for us?'];

  customElements.define(
    'size-quantity-grid-submit',
    class SizeQuantityGridSubmit extends HTMLElement {
      constructor() {
        super();

        this.cartDrawerElement = document.querySelector('cart-drawer');
        // Delegated so the listener keeps working even if something re-renders
        // the button's markup later (matches the pattern used elsewhere in
        // this theme for dynamically-updated product form controls).
        this.addEventListener('click', this.handleClick.bind(this));
      }

      get cardsContainer() {
        return document.getElementById(this.dataset.cardsId);
      }

      get button() {
        return this.querySelector('button');
      }

      get errorMessageElement() {
        return this.closest('form')?.querySelector('.product-form__error-message');
      }

      // Custom product-options apps (e.g. Hulk Product Options) inject their
      // fields into this same form using Shopify's standard line item
      // properties[Name] naming so they work with a normal form submit -
      // collect those here and apply them to every item we add, since a
      // plain items:[{id, quantity}] POST wouldn't otherwise include them.
      getLineItemProperties() {
        const form = this.closest('form');
        const properties = {};
        if (!form) return properties;

        for (const [key, value] of new FormData(form).entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (!match) continue;

          // Skip blank values (e.g. a custom-option field the shopper never
          // interacted with) - the classic single-item add sends these as
          // form-encoded data, which Shopify's cart drops when a property's
          // value is empty. Sending them through as an explicit JSON
          // properties object instead would keep them as real (if invisible)
          // properties on the line item, which is enough for some apps'
          // "has options been set" checks to wrongly say yes.
          if (typeof value === 'string' && value.trim() !== '') {
            properties[match[1]] = value;
          }
        }

        return properties;
      }

      handleClick(event) {
        const button = event.target.closest('button');
        if (!button) return;

        // Stop this button from behaving like the normal submit button it
        // still is in the DOM (kept that way so third-party apps such as
        // Hulk Product Options, which anchor on the Add to cart button/form,
        // keep working) - we run our own multi-item add instead.
        event.preventDefault();
        this.lastClickedElement = button;

        const cardsContainer = this.cardsContainer;
        if (!cardsContainer) return;

        const rows = [...cardsContainer.querySelectorAll('.quantity__input')].filter(
          (input) => Number(input.value) > 0
        );

        if (!rows.length) {
          this.showErrorMessage(FoxTheme.sizeQuantityGridStrings.noSelectionError);
          return;
        }

        const allProperties = this.getLineItemProperties();
        const hasProperties = Object.keys(allProperties).length > 0;

        const repeatedProperties = { ...allProperties };
        ONE_TIME_FEE_PROPERTIES.forEach((key) => delete repeatedProperties[key]);
        const hasRepeatedProperties = Object.keys(repeatedProperties).length > 0;


        const items = {
          items: rows.map((input, index) => {
            const item = {
              id: Number(input.dataset.quantityVariantId),
              quantity: Number(input.value),
            };
            if (index === 0) {
              if (hasProperties) item.properties = allProperties;
            } else if (hasRepeatedProperties) {
              item.properties = repeatedProperties;
            }
            return item;
          }),
        };


        if (document.body.classList.contains('cart-template') || FoxTheme.settings.cartType != 'drawer') {
          FoxTheme.utils.postLink(FoxTheme.routes.cart_add_url, {
            parameters: {
              ...items,
            },
          });
          return;
        }

        this.showErrorMessage();
        this.toggleButtonLoading(true);

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({
          ...items,
          sections: sectionsToBundle,
          sections_url: window.location.pathname,
        });

        fetch(`${FoxTheme.routes.cart_add_url}`, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.showErrorMessage(parsedState.description);
              return;
            }

            const cartJson = await (
              await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })
            ).json();
            cartJson['sections'] = parsedState['sections'];

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            document.dispatchEvent(
              new CustomEvent('product-ajax:added', {
                detail: {
                  product: parsedState,
                },
              })
            );

            rows.forEach((input) => {
              input.value = 0;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            });

            this.cartDrawerElement && this.cartDrawerElement.show(this.lastClickedElement);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.toggleButtonLoading(false);
          });
      }

      showErrorMessage(errorMessage = false) {
        const errorMessageElement = this.errorMessageElement;
        if (errorMessageElement) {
          errorMessageElement.hidden = !errorMessage;

          if (errorMessage) errorMessageElement.textContent = errorMessage;
        } else {
          errorMessage && alert(errorMessage);
        }
      }

      toggleButtonLoading(status) {
        const button = this.button;
        if (!button) return;

        if (status) {
          button.classList.add('btn--loading');
          button.setAttribute('disabled', true);
        } else {
          button.classList.remove('btn--loading');
          button.removeAttribute('disabled');
        }
      }
    }
  );
}


// Running total under the size grid (ticket 22, 2 Nov 2026): sums quantity x
// unit price for every size card and shows it once something is selected.
// Delegated on document so it keeps working when the grid block is swapped
// after a colour change.
if (!window.__sizeGridTotalInit) {
  window.__sizeGridTotalInit = true;

  const formatMoney = (pence) => {
    try {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(pence / 100);
    } catch (e) {
      return '\u00a3' + (pence / 100).toFixed(2);
    }
  };

  const updateTotals = () => {
    document.querySelectorAll('[data-size-grid-total]').forEach((el) => {
      const grid = document.getElementById(el.dataset.gridId);
      if (!grid) return;
      let pence = 0;
      let units = 0;
      grid.querySelectorAll('.quantity__input').forEach((input) => {
        const q = Number(input.value) || 0;
        const p = Number(input.dataset.unitPrice) || 0;
        if (q > 0) {
          pence += q * p;
          units += q;
        }
      });
      const value = el.querySelector('[data-size-grid-total-value]');
      if (units > 0) {
        if (value) value.textContent = formatMoney(pence) + ' (' + units + (units === 1 ? ' item)' : ' items)');
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });
  };

  ['input', 'change'].forEach((evt) =>
    document.addEventListener(evt, (e) => {
      if (e.target && e.target.closest && e.target.closest('.size-quantity-grid__cards')) updateTotals();
    })
  );
  document.addEventListener('click', (e) => {
    if (e.target && e.target.closest && e.target.closest('.size-quantity-grid__cards .quantity__button')) {
      setTimeout(updateTotals, 0);
      setTimeout(updateTotals, 150);
    }
  });
}
